import {HttpErrorResponse} from '@angular/common/http';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Router} from '@angular/router';
import {ReplaySubject} from 'rxjs';
import {finalize, takeUntil} from 'rxjs/operators';

import {Contract} from '../../../../shared/interface/contract';
import {
  GeneratedInvoiceResponse,
  GenerateInvoiceRequest,
  InvoiceGeneratorPreparation,
} from '../../../../shared/interface/invoice-generator';
import {InvoiceGeneratorService} from '../../../../shared/services/invoice-generator.service';
import moment, {Moment} from 'moment';
import {AddInitialBalanceComponent} from "../add-initial-balance/add-initial-balance.component";
import {ContractService} from "../../../../shared/services/contract.service";
import {MatDialog} from "@angular/material/dialog";
import {COMPANY_ASF, COMPANY_ASF_PREMIUM} from "../../../../shared/services/dict.service";

@Component({
  selector: 'app-invoice-generator',
  templateUrl: './invoice-generator.component.html',
  styleUrls: ['./invoice-generator.component.scss'],
})
export class InvoiceGeneratorComponent implements OnInit, OnDestroy {
  processingRequest = false;
  loadingPreparation = false;

  invoiceGeneratorForm: FormGroup;
  contractControl: FormControl;
  numberControl: FormControl;
  grossControl: FormControl;
  issueDateControl: FormControl;
  salesMonthControl: FormControl;
  bankAccountNumberControl: FormControl;
  reasonControl: FormControl;
  contractBalanceContract?: string;
  contractBalanceToPayNet?: number;
  contractBalanceInitialBalanceNet?: number;
  contractBalanceSwap?: number;
  debitNet?: number;
  contractBalanceTotalCommission?: number;
  contractBalancePaid?: number;
  addInitialBalanceButton = true;
  blockSubmit = false;

  selectedContract?: Contract;
  amountToPay: number | null = null;
  minSalesMonth: Moment;

  private preparationRequestId = 0;
  private destroyed$ = new ReplaySubject<boolean>(1);

  constructor(
    private snackBar: MatSnackBar,
    private router: Router,
    private fb: FormBuilder,
    private invoiceGeneratorService: InvoiceGeneratorService,
    private contractService: ContractService,
    private dialog: MatDialog,
  ) {
    const today = this.getToday();
    const initialSalesMonth = moment(today).startOf('month');

    this.minSalesMonth = this.calculateMinSalesMonth(today);

    this.invoiceGeneratorForm = this.fb.group({
      contract: this.contractControl = this.fb.control('', [Validators.required]),
      number: this.numberControl = this.fb.control('', [Validators.required]),
      gross: this.grossControl = this.fb.control('', [
        Validators.required,
        Validators.min(0.01),
        this.amountToPayValidator(),
      ]),
      issueDate: this.issueDateControl = this.fb.control(today, [Validators.required]),
      salesMonth: this.salesMonthControl = this.fb.control(initialSalesMonth, [Validators.required]),
      bankAccountNumber: this.bankAccountNumberControl = this.fb.control('', [Validators.required]),
      reason: this.reasonControl = this.fb.control('', [
        Validators.required,
        Validators.maxLength(1000),
      ]),
    });
  }

  ngOnInit(): void {
    this.issueDateControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((issueDate: Date | string | null) => {
        this.updateSalesMonthLimit(issueDate);

        if (this.selectedContract) {
          this.loadPreparation();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }


  get minSalesMonthLabel(): string {
    return this.minSalesMonth
      ? this.minSalesMonth.format('MM.YYYY')
      : '';
  }

  contractChanged(contract: Contract): void {
    this.selectedContract = contract;

    this.blockSubmit = this.isInvoiceGenerationBlocked(contract);
    this.setContractBalanceData(contract);

    this.clearBackendErrors([
      'contract',
      'number',
      'bankAccountNumber',
    ]);

    this.numberControl.reset('');

    this.fillValuesFromContract(contract);
    this.loadPreparation();
  }

  private setContractBalanceData(contract?: Contract): void {
    const balance = contract?.contractBalance;

    this.contractBalanceContract = contract?.uuid;
    this.contractBalanceToPayNet = balance?.toPayNet ?? 0;
    this.contractBalanceInitialBalanceNet =
      balance?.initialBalanceNet ?? 0;
    this.contractBalanceTotalCommission =
      balance?.totalCommission ?? 0;
    this.contractBalancePaid = balance?.paid ?? 0;
    this.contractBalanceSwap = balance?.swap ?? 0;
    this.debitNet = balance?.debitNet ?? 0;

    this.amountToPay = this.parseNullableNumber(
      balance?.toPayNet,
    );

    this.addInitialBalanceButton = !contract?.uuid;

    this.grossControl.updateValueAndValidity({
      emitEvent: false,
    });
  }

  save(): void {

    this.clearBackendErrors();
    this.invoiceGeneratorForm.markAllAsTouched();

    if (!this.selectedContract?.uuid) {
      this.contractControl.setErrors({
        ...(this.contractControl.errors ?? {}),
        required: true,
      });
      return;
    }

    if (this.blockSubmit) {
      this.snackBar.open(
        'Partner posiada aktywną blokadę wystawiania faktur.',
        'Zamknij',
        {duration: 6000},
      );

      return;
    }

    if (
      this.invoiceGeneratorForm.invalid ||
      this.processingRequest ||
      this.loadingPreparation
    ) {
      return;
    }

    const value = this.invoiceGeneratorForm.getRawValue();

    const request: GenerateInvoiceRequest = {
      contract: this.selectedContract.uuid,
      number: String(value.number).trim(),
      gross: Number(value.gross),
      issueDate: this.formatDate(value.issueDate),
      salesMonth: moment(value.salesMonth).format('YYYY-MM'),
      accountNumber: String(value.bankAccountNumber).trim(),
      reason: String(value.reason).trim(),
    };

    this.processingRequest = true;

    this.invoiceGeneratorService.generate(request)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => this.processingRequest = false),
      )
      .subscribe(
        (response: GeneratedInvoiceResponse) => {
          if (!response.uuid) {
            this.snackBar.open(
              'Faktura nie została wygenerowana.',
              'Zamknij',
              {duration: 6000},
            );

            return;
          }

          this.snackBar.open(
            'Faktura została wygenerowana.',
            'Zamknij',
            {duration: 4000},
          );

          this.router.navigate([
            '/faktury/szczegoly',
            response.uuid,
          ]);
        },
        (error: HttpErrorResponse) => this.handleGenerateError(error),
      );
  }

  cancel(): void {
    this.router.navigate(['/faktury']);
  }

  private fillValuesFromContract(contract: Contract): void {
    const contractData = contract as Contract & {
      selfBillingAccountNumber?: string | null;
      partner?: {
        accountNumber?: string | null;
      } | null;
      contractBalance?: {
        toPayNet?: number | string | null;
      } | null;
    };

    const bankAccountNumber =
      contractData.selfBillingAccountNumber?.trim() ||
      contractData.partner?.accountNumber?.trim() ||
      '';

    this.bankAccountNumberControl.setValue(bankAccountNumber);
    this.amountToPay = this.parseNullableNumber(
      contractData.contractBalance?.toPayNet,
    );

    this.grossControl.updateValueAndValidity({
      emitEvent: false,
    });
  }



  private loadPreparation(): void {
    if (!this.selectedContract?.uuid || !this.issueDateControl.value) {
      return;
    }
    this.addInitialBalanceButton = false;
    this.contractBalanceContract = this.selectedContract.uuid;
    this.contractBalanceToPayNet = this.selectedContract.contractBalance?.toPayNet;
    this.contractBalanceInitialBalanceNet = this.selectedContract.contractBalance?.initialBalanceNet;
    this.contractBalanceTotalCommission = this.selectedContract.contractBalance?.totalCommission;
    this.contractBalancePaid = this.selectedContract.contractBalance?.paid;
    this.contractBalanceSwap = this.selectedContract.contractBalance?.swap;
    this.debitNet = this.selectedContract.contractBalance?.debitNet;

    const requestId = ++this.preparationRequestId;
    const issueDate = this.formatDate(this.issueDateControl.value);

    this.loadingPreparation = true;
    this.clearBackendErrors(['number']);

    this.invoiceGeneratorService.prepare(this.selectedContract.uuid, issueDate)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          if (requestId === this.preparationRequestId) {
            this.loadingPreparation = false;
          }
        }),
      )
      .subscribe(
        (preparation: InvoiceGeneratorPreparation) => {
          if (requestId !== this.preparationRequestId) {
            return;
          }

          this.numberControl.setValue(preparation.number ?? '');

          // Backend może zwrócić aktualniejsze dane, ale nie są one wymagane,
          // ponieważ rachunek i kwotę do wypłaty mamy już w wybranej umowie.
          if (preparation.bankAccountNumber !== undefined) {
            this.bankAccountNumberControl.setValue(
              preparation.bankAccountNumber ?? '',
            );
          }

          if (preparation.amountToPay !== undefined) {
            this.amountToPay = this.parseNullableNumber(
              preparation.amountToPay,
            );

            this.grossControl.updateValueAndValidity({
              emitEvent: false,
            });
          }
        },
        (error: HttpErrorResponse) => {
          if (requestId !== this.preparationRequestId) {
            return;
          }

          this.handlePreparationError(error);
        },
      );
  }

  private handlePreparationError(error: HttpErrorResponse): void {
    const backendErrors = error.error?.list ?? {};
    const errorMessage =
      backendErrors.number ??
      backendErrors.invoiceNumber ??
      error.error?.text ??
      'Nie udało się ustalić kolejnego numeru faktury.';

    this.numberControl.setErrors({
      ...(this.numberControl.errors ?? {}),
      backendError: this.normalizeErrorMessage(errorMessage),
    });
    this.numberControl.markAsTouched();
  }

  private handleGenerateError(error: HttpErrorResponse): void {
    const backendErrors = error.error?.list ?? {};
    const fieldMap: {[key: string]: string} = {
      contractUuid: 'contract',
      amount: 'gross',
      saleMonth: 'salesMonth',
      accountNumber: 'bankAccountNumber',
    };

    Object.keys(backendErrors).forEach((backendField: string) => {
      const formField = fieldMap[backendField] ?? backendField;
      const control = this.invoiceGeneratorForm.get(formField);

      if (!control) {
        return;
      }

      control.setErrors({
        ...(control.errors ?? {}),
        backendError: this.normalizeErrorMessage(backendErrors[backendField]),
      });
      control.markAsTouched();
    });

    this.snackBar.open(
      error.error?.text ??
      'Nie udało się wygenerować faktury. Sprawdź formularz i spróbuj ponownie.',
      'Zamknij',
      {duration: 6000},
    );
  }

  private updateSalesMonthLimit(
    issueDate: Date | string | null,
  ): void {
    if (!issueDate) {
      this.salesMonthControl.reset('');
      return;
    }

    const date = this.toDate(issueDate);

    if (!date) {
      return;
    }

    this.minSalesMonth = this.calculateMinSalesMonth(date);

    const currentSalesMonth = moment.isMoment(
      this.salesMonthControl.value,
    )
      ? this.salesMonthControl.value.clone()
      : moment(this.salesMonthControl.value, 'YYYY-MM', true);

    if (
      !currentSalesMonth.isValid() ||
      currentSalesMonth.isBefore(this.minSalesMonth, 'month')
    ) {
      this.salesMonthControl.setValue(
        this.minSalesMonth.clone(),
      );
    }
  }


  private calculateMinSalesMonth(issueDate: Date): Moment {
    const currentMonth = moment(issueDate).startOf('month');

    if (issueDate.getDate() <= 15) {
      return currentMonth.subtract(1, 'month');
    }

    return currentMonth;
  }

  private clearBackendErrors(fields?: string[]): void {
    const controls = fields
      ? fields.map((field: string) => this.invoiceGeneratorForm.get(field))
      : Object.keys(this.invoiceGeneratorForm.controls)
        .map((field: string) => this.invoiceGeneratorForm.get(field));

    controls.forEach(control => {
      if (!control?.errors?.backendError) {
        return;
      }

      const errors = {...control.errors};
      delete errors.backendError;
      control.setErrors(Object.keys(errors).length > 0 ? errors : null);
    });
  }

  private normalizeErrorMessage(error: unknown): string {
    if (Array.isArray(error)) {
      return error.join(' ');
    }

    if (typeof error === 'object' && error !== null) {
      return Object.values(error).join(' ');
    }

    return String(error);
  }

  private parseNullableNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const normalizedValue = typeof value === 'string'
      ? value.replace(',', '.')
      : value;
    const parsedValue = Number(normalizedValue);

    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  private formatDate(value: Date | string): string {
    const date = this.toDate(value);

    if (!date) {
      return '';
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  private formatMonth(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }

  private toDate(value: Date | string): Date | null {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private getToday(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  resetForm(): void {
    const today = this.getToday();
    const initialSalesMonth = moment(today).startOf('month');
    const minSalesMonth = this.calculateMinSalesMonth(today);

    this.preparationRequestId++;

    this.selectedContract = undefined;
    this.blockSubmit = false;
    this.amountToPay = null;
    this.loadingPreparation = false;
    this.minSalesMonth = minSalesMonth;

    this.contractBalanceContract = undefined;
    this.contractBalanceToPayNet = undefined;
    this.contractBalanceInitialBalanceNet = undefined;
    this.contractBalanceSwap = undefined;
    this.debitNet = undefined;
    this.contractBalanceTotalCommission = undefined;
    this.contractBalancePaid = undefined;
    this.addInitialBalanceButton = true;

    this.invoiceGeneratorForm.reset(
      {
        contract: '',
        number: '',
        gross: '',
        issueDate: today,
        salesMonth: initialSalesMonth.clone(),
        bankAccountNumber: '',
        reason: '',
      },
      {
        emitEvent: false,
      },
    );

    this.invoiceGeneratorForm.markAsPristine();
    this.invoiceGeneratorForm.markAsUntouched();
  }
  private amountToPayValidator(): ValidatorFn {
    return (
      control: AbstractControl,
    ): ValidationErrors | null => {
      if (
        control.value === null ||
        control.value === '' ||
        this.amountToPay === null
      ) {
        return null;
      }

      const gross = Number(control.value);

      if (!Number.isFinite(gross)) {
        return null;
      }

      const grossInCents = Math.round(gross * 100);
      const amountToPayInCents = Math.round(
        this.amountToPay * 100
      );

      return grossInCents > amountToPayInCents
        ? {
          amountExceedsPayout: {
            max: this.amountToPay,
            actual: gross,
          },
        }
        : null;
    };
  }


  addInitialBalance(): void{
    const dialogRef = this.dialog.open(AddInitialBalanceComponent, {
      width: '50vw',
      maxHeight: '90vh',
      data: {contract: this.contractBalanceContract}
    });
    dialogRef.afterClosed().subscribe( result => {
      if (result) {
        this.grossControl.reset();
        this.loadContractBalance();
        this.loadPreparation();
      }
    });
  }


  loadContractBalance(): void {
    if (!this.contractBalanceContract) {
      return;
    }

    this.contractService.get(this.contractBalanceContract)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((contract: Contract) => {
        this.selectedContract = contract;
        this.setContractBalanceData(contract);
      });
  }

  private isInvoiceGenerationBlocked(
    contract: Contract,
  ): boolean {
    const partner = contract.partner;
    const companyKey = contract.company?.key;

    if (!partner) {
      return false;
    }

    return (
      companyKey === COMPANY_ASF &&
      partner.invoiceAddBlockAsf === true
    ) || (
      companyKey === COMPANY_ASF_PREMIUM &&
      partner.invoiceAddBlockPremium === true
    );
  }

}
